import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { EChartsOption } from 'echarts';
import { IdentityScoreData, ScoreFactor } from '../../../../services/identity-security.service';

@Component({
  selector: 'app-posture-breakdown',
  standalone: true,
  imports: [CommonModule, NgxEchartsModule, TooltipModule],
  templateUrl: './posture-breakdown.component.html',
  styleUrl: './posture-breakdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PostureBreakdownComponent {
  identityScore = input<IdentityScoreData | null>(null);
  isLoading = input<boolean>(true);
  // `exposureMetrics` used to be an input here and this was its only consumer in the
  // module. The Affected scope section now reads `detail` off the same factors the chart
  // ranks, so the whole `exposure_metrics` half of the score payload has no reader in the
  // frontend. Left in the API contract rather than removed from it.

  private readonly MAX_CHART_FACTORS = 5;

  /**
   * Severity chip classes, from the shared palette every other chip in the app uses.
   *
   * The section is specified to show the check's severity and its detection type. It
   * used to show exploitation likelihood and attack path, which are different catalog
   * fields: likelihood is how probable an attack is, severity is what the check is
   * worth, and the bar beside it is weighted by the second.
   */
  public getSeverityClass(severity: string | null | undefined): string {
    const map: Record<string, string> = {
      Critical: 'severity-badge severity-critical',
      High: 'severity-badge severity-high',
      Medium: 'severity-badge severity-medium',
      Low: 'severity-badge severity-low',
    };
    return map[severity ?? ''] ?? 'severity-badge severity-low';
  }

  /**
   * Bar colour, from the check's severity rather than from the size of its impact.
   *
   * The bar used to be red above an impact of 10 and amber below it, which said the
   * same thing as its own length and contradicted the chip beside it: a Critical check
   * with a small impact drew amber and a Medium with a large one drew red. Production
   * carries all four severities among failing checks - 126 Medium, 77 High, 33 Low, 28
   * Critical - and impacts from 3 to 32, so the two orderings disagree often.
   *
   * The catalog holds no colours; this reads the same CSS variables as every severity
   * chip in the module.
   */
  private getSeverityColor(severity: string | null | undefined): string {
    const token: Record<string, string> = {
      Critical: '--severity-critical-fg',
      High: '--severity-high-fg',
      Medium: '--severity-medium-fg',
      Low: '--severity-low-fg',
    };
    return this.getCssVar(token[severity ?? ''] ?? '--severity-low-fg');
  }

  private getCssVar(name: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  /**
   * Only failing checks that deduct (not accepted), sorted by score impact descending,
   * capped at 5.
   *
   * Impact order, not exploitation likelihood. The chart below reads this same list and
   * its bars ARE the impact, so any other order leaves a short bar between two long ones
   * with nothing on screen explaining why. Likelihood is already grouped visually by the
   * chip colour beside each row.
   */
  public topFailingFactors = computed<ScoreFactor[]>(() => {
    const score = this.identityScore();
    if (!score?.factors?.length) return [];
    return score.factors
      .filter((f) => f.status === 'fail' && !f.accepted)
      .sort((a, b) => b.score_impact - a.score_impact)
      .slice(0, this.MAX_CHART_FACTORS);
  });

  public totalFailingCount = computed<number>(() => {
    const score = this.identityScore();
    if (!score?.factors?.length) return 0;
    return score.factors.filter((f) => f.status === 'fail' && !f.accepted).length;
  });

  public chartOptions = computed<EChartsOption>(() => {
    const factors = this.topFailingFactors();
    if (!factors.length) return {};

    const categories = factors.map((f) => f.name);
    const values = factors.map((f) => f.score_impact);

    const gray700 = this.getCssVar('--gray-700');
    const gray600 = this.getCssVar('--gray-600');

    return {
      backgroundColor: 'transparent',
      grid: {
        left: 10,
        right: 50,
        top: 10,
        bottom: 10,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        show: false,
        // Not a fixed 20. score_impact is weight / total evaluated weight, so the
        // fewer checks a tenant can evaluate, the larger each remaining one gets:
        // with all ten evaluated the ceiling is 19, but a production organization
        // with several unevaluated checks already reaches 24, and a hardcoded 20
        // silently clipped that bar — the most severe one on the chart. Keeping 20
        // as the floor stops a healthy tenant's single small bar from filling the
        // width.
        max: Math.max(20, ...values),
      },
      yAxis: {
        type: 'category',
        data: categories,
        // Factors are sorted highest-impact first; inverse puts that bar at the top.
        inverse: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontSize: 13,
          color: gray700,
          fontWeight: 500,
        },
      },
      series: [
        {
          type: 'bar',
          data: factors.map((f) => ({
            value: f.score_impact,
            itemStyle: {
              color: this.getSeverityColor(f.severity),
              borderRadius: [0, 4, 4, 0],
            },
          })),
          barWidth: 20,
          label: {
            show: true,
            position: 'right',
            formatter: '-{c}%',
            fontSize: 12,
            fontWeight: 600,
            color: gray600,
          },
        },
      ],
    };
  });
}
